/**
 * ScheduleVersionsDrawer — lists all uploaded schedule versions (from any
 * source: Excel / MSP / XER / XML) and lets the PM diff two versions
 * side-by-side.
 */
import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, GitCompare, FileText, TrendingUp, TrendingDown, Minus, ArrowRight } from 'lucide-react';

const fmtDelta = (n) => (n > 0 ? `+${n}d` : n < 0 ? `${n}d` : '0d');
const fmtDate = (d) => (d ? String(d).slice(0, 10) : '—');

export default function ScheduleVersionsDrawer({ open, onClose, projectId }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pickA, setPickA] = useState(null);
  const [pickB, setPickB] = useState(null);
  const [compareResult, setCompareResult] = useState(null);
  const [tab, setTab] = useState('changed');
  const [comparing, setComparing] = useState(false);

  useEffect(() => {
    if (!open || !projectId) return;
    setLoading(true);
    api.get(`/scheduling/projects/${projectId}/versions`)
      .then((r) => setVersions(r.data?.versions || []))
      .catch(() => setVersions([]))
      .finally(() => setLoading(false));
  }, [open, projectId]);

  const compare = async () => {
    if (!pickA || !pickB || pickA === pickB) return;
    setComparing(true);
    try {
      const r = await api.post(`/scheduling/projects/${projectId}/versions/${pickA}/compare/${pickB}`);
      setCompareResult(r.data);
      setTab('changed');
    } catch (e) { /* ignore */ }
    setComparing(false);
  };

  const summary = compareResult?.summary || {};
  const rows = compareResult ? (tab === 'changed' ? compareResult.changed : tab === 'added' ? compareResult.added : compareResult.removed) : [];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col"
                     style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}
                     data-testid="schedule-versions-drawer">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <GitCompare className="w-5 h-5 text-blue-400" />
            Schedule Versions · Compare Uploads
          </DialogTitle>
          <DialogDescription style={{ color: 'var(--text-muted)' }}>
            Every schedule upload (Excel / MSP / XER / XML) is snapshotted automatically. Pick any two versions to see what changed.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto space-y-3">
          {/* Version list */}
          <div className="rounded-lg border" style={{ borderColor: 'var(--border-color)' }}>
            <div className="px-3 py-2 border-b flex items-center justify-between"
                 style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg-alt)' }}>
              <div className="text-[11px] font-semibold" style={{ color: 'var(--text-primary)' }}>
                {loading ? 'Loading…' : `${versions.length} version${versions.length === 1 ? '' : 's'} on record`}
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={compare} disabled={!pickA || !pickB || pickA === pickB || comparing}
                        className="h-7 text-[11px] bg-blue-600 hover:bg-blue-500 text-white"
                        data-testid="version-compare-btn">
                  {comparing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <ArrowRight className="w-3.5 h-3.5 mr-1" />}
                  Compare
                </Button>
              </div>
            </div>
            <div className="max-h-[220px] overflow-y-auto">
              <table className="w-full text-[10px]">
                <thead className="sticky top-0" style={{ background: 'var(--card-bg-alt)' }}>
                  <tr>
                    <th className="p-1.5 text-left w-8" />
                    <th className="p-1.5 text-left w-8" />
                    <th className="p-1.5 text-left" style={{ color: 'var(--text-muted)' }}>Version</th>
                    <th className="p-1.5 text-left" style={{ color: 'var(--text-muted)' }}>Uploaded</th>
                    <th className="p-1.5 text-left" style={{ color: 'var(--text-muted)' }}>Source</th>
                    <th className="p-1.5 text-right" style={{ color: 'var(--text-muted)' }}>Tasks</th>
                    <th className="p-1.5 text-left" style={{ color: 'var(--text-muted)' }}>By</th>
                  </tr>
                </thead>
                <tbody>
                  {versions.map((v) => (
                    <tr key={v.version_id} className="border-t hover:bg-white/5"
                        style={{ borderColor: 'var(--border-color)' }}
                        data-testid={`version-row-${v.version_number}`}>
                      <td className="p-1.5 text-center">
                        <input type="radio" name="pickA" checked={pickA === v.version_id}
                               onChange={() => setPickA(v.version_id)} data-testid={`version-a-${v.version_number}`} />
                      </td>
                      <td className="p-1.5 text-center">
                        <input type="radio" name="pickB" checked={pickB === v.version_id}
                               onChange={() => setPickB(v.version_id)} data-testid={`version-b-${v.version_number}`} />
                      </td>
                      <td className="p-1.5">
                        <Badge className="font-mono text-[9px] bg-slate-700 text-slate-200">v{v.version_number}</Badge>
                      </td>
                      <td className="p-1.5" style={{ color: 'var(--text-primary)' }}>{fmtDate(v.uploaded_at)}</td>
                      <td className="p-1.5"><Badge className="text-[9px] bg-blue-500/20 text-blue-300 uppercase">{v.source_format}</Badge></td>
                      <td className="p-1.5 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{v.total_tasks}</td>
                      <td className="p-1.5 truncate max-w-[140px]" style={{ color: 'var(--text-muted)' }}>{v.uploaded_by}</td>
                    </tr>
                  ))}
                  {!loading && versions.length === 0 && (
                    <tr><td colSpan={7} className="p-4 text-center" style={{ color: 'var(--text-muted)' }}>
                      No versions yet — upload an MSP / XER / Excel schedule to seed the first snapshot.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Compare Result */}
          {compareResult && (
            <div className="rounded-lg border" data-testid="version-compare-result"
                 style={{ borderColor: 'var(--border-color)' }}>
              <div className="px-3 py-2 border-b flex items-center gap-3 flex-wrap"
                   style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg-alt)' }}>
                <div className="text-[10px] font-mono" style={{ color: 'var(--text-primary)' }}>
                  v{compareResult.from.version_number} <ArrowRight className="inline w-3 h-3 mx-1" /> v{compareResult.to.version_number}
                </div>
                <Badge className="text-[9px] bg-emerald-500/20 text-emerald-300">+{summary.added} added</Badge>
                <Badge className="text-[9px] bg-red-500/20 text-red-300">-{summary.removed} removed</Badge>
                <Badge className="text-[9px] bg-amber-500/20 text-amber-300">{summary.changed} changed</Badge>
                {summary.maxShiftDays > 0 && <Badge className="text-[9px] bg-purple-500/20 text-purple-300">max shift {summary.maxShiftDays}d</Badge>}
                <div className="flex-1" />
                {['changed', 'added', 'removed'].map((t) => (
                  <button key={t} onClick={() => setTab(t)}
                          className="text-[10px] px-2 py-0.5 rounded"
                          style={{ background: tab === t ? 'var(--accent-primary)' : 'transparent',
                                   color: tab === t ? '#fff' : 'var(--text-muted)' }}
                          data-testid={`version-tab-${t}`}>{t}</button>
                ))}
              </div>
              <div className="max-h-[300px] overflow-y-auto">
                <table className="w-full text-[10px]">
                  <thead className="sticky top-0" style={{ background: 'var(--card-bg-alt)' }}>
                    <tr>
                      <th className="p-1.5 text-left" style={{ color: 'var(--text-muted)' }}>WBS</th>
                      <th className="p-1.5 text-left" style={{ color: 'var(--text-muted)' }}>Task</th>
                      <th className="p-1.5 text-right" style={{ color: 'var(--text-muted)' }}>Δ Start</th>
                      <th className="p-1.5 text-right" style={{ color: 'var(--text-muted)' }}>Δ Finish</th>
                      <th className="p-1.5 text-left" style={{ color: 'var(--text-muted)' }}>Changed fields</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, 200).map((r, i) => (
                      <tr key={r.id || i} className="border-t" style={{ borderColor: 'var(--border-color)' }}>
                        <td className="p-1.5 font-mono" style={{ color: 'var(--accent-primary)' }}>{r.wbs || '—'}</td>
                        <td className="p-1.5 truncate max-w-[280px]" style={{ color: 'var(--text-primary)' }}>{r.name}</td>
                        <td className="p-1.5 text-right font-mono"
                            style={{ color: r.dStart > 0 ? '#ef4444' : r.dStart < 0 ? '#22c55e' : 'var(--text-muted)' }}>
                          {tab === 'changed' ? fmtDelta(r.dStart || 0) : '—'}
                        </td>
                        <td className="p-1.5 text-right font-mono"
                            style={{ color: r.dFinish > 0 ? '#ef4444' : r.dFinish < 0 ? '#22c55e' : 'var(--text-muted)' }}>
                          {tab === 'changed' ? fmtDelta(r.dFinish || 0) : '—'}
                        </td>
                        <td className="p-1.5 truncate max-w-[240px]" style={{ color: 'var(--text-muted)' }}>
                          {tab === 'changed' ? Object.keys(r.diffs || {}).join(', ') : (tab === 'added' ? `${fmtDate(r.start)} → ${fmtDate(r.finish)}` : `${fmtDate(r.start)} → ${fmtDate(r.finish)}`)}
                        </td>
                      </tr>
                    ))}
                    {rows.length === 0 && (
                      <tr><td colSpan={5} className="p-4 text-center" style={{ color: 'var(--text-muted)' }}>No {tab} tasks.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
